'use client';

import { VoteType } from '@prisma/client';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Clock, X } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CommentData, ReportWithDetails } from '../../../types/shared';
import { RealtimeProvider, useRealtimeVotes } from '../contexts/RealtimeProvider';
import { getTrafficReportDetails } from '../lib/actions/trafficActions';
import { TrafficCommentsSection } from './TrafficCommentsSection';
import { TrafficVoteButtons } from './TrafficVoteButtons';

const EVENT_TYPE_STYLES: Record<string, { text: string; className: string }> = {
  ACCIDENT: { text: 'Авария', className: 'bg-red-500/20 text-red-300 border-red-500/30' },
  CONGESTION: { text: 'Затор', className: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  ROAD_WORKS: { text: 'Ремонт', className: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  OTHER: { text: 'Другое', className: 'bg-slate-500/20 text-slate-300 border-slate-500/30' },
};

function ModalContent({
  report,
  onVote,
}: {
  report: ReportWithDetails;
  onVote: (reportId: string, voteType: VoteType) => void;
}) {
  const { likes, dislikes, myVote, optimisticVoteUpdate } = useRealtimeVotes();

  const handleVote = useCallback(
    (voteType: VoteType) => {
      optimisticVoteUpdate(voteType);
      onVote(report.id, voteType);
    },
    [optimisticVoteUpdate, onVote, report.id],
  );

  return (
    <>
      <div className="p-3 sm:p-4 border-b border-slate-700 flex flex-shrink-0">
        <TrafficVoteButtons likes={likes} dislikes={dislikes} myVote={myVote} onVote={handleVote} />
      </div>

      <div className="flex-grow overflow-y-auto p-3 sm:p-4 bg-slate-900 custom-scrollbar">
        <TrafficCommentsSection reportId={report.id} />
      </div>
    </>
  );
}

export function TrafficReportModal({
  report,
  onClose,
  onVote,
}: {
  report: ReportWithDetails;
  onClose: () => void;
  onVote: (reportId: string, voteType: VoteType) => void;
}) {
  const modalRef = useRef<HTMLDivElement>(null);

  const [loadedMyVote, setLoadedMyVote] = useState<VoteType | null | undefined>(undefined);
  const [loadedComments, setLoadedComments] = useState<CommentData[] | null>(null);

  const eventStyle = EVENT_TYPE_STYLES[report.type] || EVENT_TYPE_STYLES.OTHER;

  const formattedDate = useMemo(
    () => format(new Date(report.createdAt), 'd MMMM yyyy, HH:mm', { locale: ru }),
    [report.createdAt],
  );

  // ✅ Загружаем И myVote, И comments (история из БД)
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const data = await getTrafficReportDetails(report.id);

        if (!alive) return;

        setLoadedMyVote(data?.myVote ?? null);
        setLoadedComments((data?.comments ?? []) as CommentData[]);
      } catch {
        if (!alive) return;
        setLoadedMyVote(null);
        setLoadedComments([]);
      }
    })();

    return () => {
      alive = false;
    };
  }, [report.id]);

  const initialRealtimeData = useMemo(
    () => ({
      likes: report.likes,
      dislikes: report.dislikes,
      myVote: loadedMyVote ?? null,
      comments: loadedComments ?? [], // ✅ ключевое
    }),
    [report.likes, report.dislikes, loadedMyVote, loadedComments],
  );

  useEffect(() => {
    modalRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isLoading = loadedMyVote === undefined || loadedComments === null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-white/70 hover:text-white z-50 transition-colors p-1 rounded-full"
      >
        <X size={28} />
      </button>

      <motion.div
        ref={modalRef}
        layoutId={`card-${report.id}`}
        className="w-full h-full max-w-6xl max-h-[95vh] flex flex-col lg:flex-row gap-0 rounded-xl overflow-hidden shadow-2xl bg-slate-900 focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Левая часть */}
        <div className="lg:w-2/3 bg-black flex items-center justify-center relative flex-shrink-0">
          {report.imageUrl ? (
            <Image
              src={report.imageUrl}
              alt={report.comment || 'Фото'}
              fill
              className="object-contain"
              sizes="(max-width: 1200px) 100vw, 65vw"
              priority
            />
          ) : (
            <div className="text-slate-500 text-center p-8">
              <p className="text-2xl font-bold">Фото не добавлено</p>
            </div>
          )}
        </div>

        {/* Правая часть */}
        <div className="lg:w-1/3 flex flex-col h-full min-h-0 bg-slate-900 border-l border-slate-800">
          <div className="p-3 sm:p-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-start gap-3">
              <Image
                src={report.author.image || '/default-avatar.jpg'}
                alt={report.author.name || ''}
                width={40}
                height={40}
                className="rounded-full"
              />
              <div>
                <p className="font-bold text-base sm:text-lg text-white">{report.author.name}</p>
                <p className="text-sm text-slate-400 flex items-center gap-1.5">
                  <Clock size={14} /> {formattedDate}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <div
                className={`inline-block px-3 py-1 text-sm font-semibold rounded-full border ${eventStyle.className}`}
              >
                {eventStyle.text}
              </div>
              {report.comment && (
                <p className="text-slate-200 text-base sm:text-lg mt-2 whitespace-pre-wrap">
                  {report.comment}
                </p>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="flex-grow flex items-center justify-center text-slate-500">
              Загрузка...
            </div>
          ) : (
            <RealtimeProvider reportId={report.id} initialData={initialRealtimeData}>
              <ModalContent report={report} onVote={onVote} />
            </RealtimeProvider>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
